/**
 * Formats a social handle or URL into a valid, openable URL.
 * If the input is already a full URL, it returns it as is (ensuring it has a protocol).
 * Otherwise, it prefixes it with the base URL for the platform.
 */
export const formatSocialUrl = (
  platform: string,
  handleOrUrl: string | null | undefined,
): string | null => {
  if (!handleOrUrl) return null;

  const trimmed = handleOrUrl.trim();
  if (trimmed === '') return null;

  // If it's already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Handle cases like "www.facebook.com/..." or "facebook.com/..."
  if (
    trimmed.startsWith('www.') ||
    trimmed.toLowerCase().startsWith('facebook.com') ||
    trimmed.toLowerCase().startsWith('instagram.com') ||
    trimmed.toLowerCase().startsWith('twitter.com') ||
    trimmed.toLowerCase().startsWith('x.com') ||
    trimmed.toLowerCase().startsWith('youtube.com') ||
    trimmed.toLowerCase().startsWith('tiktok.com') ||
    trimmed.toLowerCase().startsWith('linkedin.com')
  ) {
    return `https://${trimmed}`;
  }

  // Platform specific base URLs
  switch (platform.toLowerCase()) {
    case 'facebook':
      return `https://facebook.com/${trimmed}`;
    case 'instagram':
      return `https://instagram.com/${trimmed}`;
    case 'twitter':
    case 'x':
      return `https://twitter.com/${trimmed}`;
    case 'youtube':
      return `https://youtube.com/${trimmed}`;
    case 'tiktok':
      return `https://tiktok.com/@${trimmed}`;
    case 'linkedin':
      if (trimmed.includes('company/')) {
        return `https://linkedin.com/${trimmed}`;
      }
      return `https://linkedin.com/company/${trimmed}`;
    case 'website':
    case 'globe':
      return `https://${trimmed}`;
    default:
      return trimmed;
  }
};
